import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header';
import { fetchAllPools } from '../../services/defiService';
import Filters from './Filters';
import PoolsList from './PoolsList';
import { getMinScoreByGrade } from './gradeUtils';

export default function PoolsCompare() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedToken, setSelectedToken] = useState('');
  const [selectedChain, setSelectedChain] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [sortBy, setSortBy] = useState('apy');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const sortDropdownRef = useRef(null);

  // 加载 pools
  useEffect(() => {
    async function loadPools() {
      try {
        setLoading(true);
        setError(null);
        const minScore = getMinScoreByGrade(selectedGrade);
        const result = await fetchAllPools({
          token: selectedToken || null,
          chain: selectedChain || null,
          protocolId: selectedProtocol,
          minScore: minScore,
          sortBy: sortBy,
          page: currentPage,
          pageSize: 100
        });
        
        if (result.success === false || result.error) {
          throw new Error(result.error || "Failed to load pools");
        }
        
        setPools(result.pools || []);
        setPagination(result.pagination);
        
        // 提取唯一协议列表（从所有已加载的 pools）
        const uniqueProtocols = Array.from(
          new Map(
            (result.pools || [])
              .filter(p => p.protocol_id && p.protocol_name)
              .map(p => [p.protocol_id, {
                id: p.protocol_id,
                name: p.protocol_name,
                slug: p.protocol_slug,
                logo: p.protocol_logo
              }])
          ).values()
        );
        setProtocols(prev => {
          const merged = new Map();
          prev.forEach(p => merged.set(p.id, p));
          uniqueProtocols.forEach(p => merged.set(p.id, p));
          return Array.from(merged.values());
        });
      } catch (err) {
        console.error('[PoolsCompare] Error loading pools:', err);
        setError(err.message || "Failed to load pools");
      } finally {
        setLoading(false);
      }
    }
    
    loadPools();
  }, [selectedToken, selectedChain, selectedProtocol, selectedGrade, sortBy, currentPage]);
  
  // 筛选条件变化时重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedToken, selectedChain, selectedProtocol, selectedGrade, sortBy]);

  // 点击外部关闭排序下拉
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortOptions = [
    { value: 'apy', label: 'By APY' },
    { value: 'score', label: 'By Score' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-gray-600">Loading pools...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Yield Comparison</h1>
          <p className="text-gray-600">Compare APY pools across protocols</p>
        </div>

        <Filters
          selectedToken={selectedToken}
          setSelectedToken={setSelectedToken}
          selectedChain={selectedChain}
          setSelectedChain={setSelectedChain}
          selectedProtocol={selectedProtocol}
          setSelectedProtocol={setSelectedProtocol}
          selectedGrade={selectedGrade}
          setSelectedGrade={setSelectedGrade}
          protocols={protocols}
        />

        {/* Sort By */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700">Sort By:</label>
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all flex items-center gap-2"
              >
                <span>{sortOptions.find(opt => opt.value === sortBy)?.label || 'By APY'}</span>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSortDropdown && (
                <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg min-w-[150px]">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortBy === option.value ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <PoolsList pools={pools} sortBy={sortBy} />
        
        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {pagination.currentPage} / {pagination.totalPages}
              {' '}(Total {pagination.totalItems} items)
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
              disabled={currentPage >= pagination.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

